import { EditPencil, Group, Plus } from 'iconoir-react'
import { useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/ui/page-header'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { useListCopy } from 'app/hooks/useListCopy'
import type { SearchResponse } from 'app/services/search'
import type { StaffMember } from './useStaff'

type StaffHit = SearchResponse<StaffMember>['hits'][number]

export type StaffShadcnComponentProps = {
  staff: SearchResponse<StaffMember> | undefined
  canManageStaff: boolean
  onCreateStaff: () => void
  onEditStaff: (staff: StaffHit) => void
  renderUserCell?: (userId: string) => React.ReactNode
}

export function StaffShadcnComponent({
  staff,
  canManageStaff,
  onCreateStaff,
  onEditStaff,
  renderUserCell,
}: StaffShadcnComponentProps) {
  const copy = useListCopy()
  const columns = useMemo(
    (): ColumnDef<StaffHit, any>[] => [
      {
        id: `name`,
        accessorKey: `name`,
        header: `Name`,
        cell: (props) => (
          <div className="group flex h-12 items-center gap-2">
            <span
              className={canManageStaff ? `cursor-pointer hover:underline` : undefined}
              onClick={canManageStaff ? () => onEditStaff(props.row.original) : undefined}
            >
              {props.getValue()}
            </span>
            {canManageStaff && (
              <div
                className="flex h-6 w-6 cursor-pointer items-center justify-center rounded border border-strong bg-surface-subtle p-1.5 opacity-0 group-hover:opacity-100"
                onClick={() => onEditStaff(props.row.original)}
              >
                <EditPencil className="text-strong" />
              </div>
            )}
          </div>
        ),
        meta: { defaultVisible: true },
      },
      {
        id: `userId`,
        accessorKey: `userId`,
        header: `Associated User`,
        cell: (props) => <>{props.getValue() && renderUserCell ? renderUserCell(props.getValue()) : null}</>,
        meta: { defaultVisible: true },
      },
      {
        id: `aliases`,
        accessorKey: `aliases`,
        header: `Aliases`,
        minSize: 200,
        cell: (props) => <>{props.getValue()?.join(`, `)}</>,
        meta: { defaultVisible: true },
      },
    ],
    [canManageStaff, onEditStaff, renderUserCell],
  )

  return (
    <PageScroll
      variant="table"
      header={
        <PageHeader
          title="Staff"
          primaryButtonSlot={
            canManageStaff ? (
              <Button variant="primary" onClick={onCreateStaff}>
                <Plus strokeWidth={2} width={16} height={16} />
                Add New Staff
              </Button>
            ) : undefined
          }
        />
      }
    >
      <SimpleTable
        columns={columns}
        data={staff?.hits}
        hideFooter
        edgePadded
        emptyState={
          <TableEmptyState
            icon={<Group className="h-6 w-6" strokeWidth={1.5} />}
            title="No staff members yet"
            description={copy.staffDescription}
            action={
              canManageStaff ? (
                <Button variant="primary" size="sm" onClick={onCreateStaff}>
                  <Plus strokeWidth={2} width={16} height={16} />
                  Add New Staff
                </Button>
              ) : undefined
            }
          />
        }
      />
    </PageScroll>
  )
}
